import { Chip, Link, Tooltip, useTheme } from "@mui/material";
import moment from "moment";
import { Fragment } from "react";
import InfoIcon from "@mui/icons-material/Info";
import LockIcon from "@mui/icons-material/Lock";
import {
  Button,
  ButtonGroup,
  Menu,
  MultipleAutocomplete,
  MultipleAutocompleteOption,
  Table,
  Typography,
  Input,
} from "../../../components";
import currency from "currency.js";
import {
  formatNumberCompact,
  getBidStatusColor,
  getBidStatusLabel,
} from "../../../utils";
import TagManager from "react-gtm-module";
import TelegramIcon from "@mui/icons-material/Telegram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { COMPANY_OPTIONS, PRIORITY_OPTIONS } from "../../../constants";
import {
  GridFilterInputValueProps,
  GridFilterItem
} from "@mui/x-data-grid-pro";
import {  GridFilterOperator } from "@mui/x-data-grid-pro";
import { useNavigate } from "react-router-dom";
import { useAxios } from "../../../hooks";
import { APP_NAME } from "@next-pontos/settings";
import { User } from "../../../context/auth";

interface BidsTableProps {
  me: User | null
  refetchBids: () => void;
  bids: any;
  setDenyBidId: (id: string) => void;
  denyModalVisibilityControls: {
    setTrue: () => void;
  };
  setDeleteBidId: (id: string) => void;
  deleteModalVisibilityControls: {
    setTrue: () => void;
  };
  fetchBid: (config: any) => void;
  accountModalVisibilityControls: {
    setTrue: () => void;
  };
}

export const BidsTable = ({
  me,
  bids,
  refetchBids,
  denyModalVisibilityControls,
  setDenyBidId,
  deleteModalVisibilityControls,
  setDeleteBidId,
  accountModalVisibilityControls,
  fetchBid
}: BidsTableProps) => {
  const statusOptions = [
    {
      value: "PENDING",
      label: "Pendente",
    },
    { value: "ACCEPTED", label: "Aceita" },
    { value: "DENIED", label: "Recusada" },
    { value: "AWAITING_ORDER", label: "Aguardando Emissão" },
    { value: "ON_ORDER", label: "Em Emissão" },
    { value: "DONE", label: "Finalizada" },
  ];

  function PrioritySelector(props: GridFilterInputValueProps) {
    const { item, applyValue } = props;

    return (
      <MultipleAutocomplete
        variant="standard"
        label="Prioridade"
        options={PRIORITY_OPTIONS}
        value={PRIORITY_OPTIONS.filter((option) =>
          item?.value?.includes(option.value)
        )}
        onChange={(value) => {
          applyValue({
            ...item,
            value: (value as unknown as MultipleAutocompleteOption[]).map(
              (item) => item.value
            ),
          });
        }}
      />
    );
  }

  const prioritySelectorOperator: GridFilterOperator[] = [
    {
      label: "é igual a",
      value: "in",
      getValueAsString: (values) => {
        return values
          .map(
            (item: string) =>
              PRIORITY_OPTIONS.find(({ value }) => value === item)?.label
          )
          .join(", ");
      },
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (
          !Array.isArray(filterItem.value) ||
          filterItem.value.length === 0 ||
          !filterItem.value
        ) {
          return null;
        }

        return ({ value }) => {
          return filterItem.value.includes(value);
        };
      },
      InputComponent: PrioritySelector,
    },
  ];

  function StatusSelector(props: GridFilterInputValueProps) {
    const { item, applyValue } = props;

    return (
      <MultipleAutocomplete
        variant="standard"
        label="Status"
        options={statusOptions}
        value={statusOptions.filter((option) =>
          item?.value?.includes(option.value)
        )}
        onChange={(value) => {
          applyValue({
            ...item,
            value: (value as unknown as MultipleAutocompleteOption[]).map(
              (item) => item.value
            ),
          });
        }}
      />
    );
  }

  const statusSelectorOperator: GridFilterOperator[] = [
    {
      label: "é igual a",
      value: "in",
      getValueAsString: (values) => {
        return values
          .map(
            (item: string) =>
              statusOptions.find(({ value }) => value === item)?.label
          )
          .join(", ");
      },
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (
          !Array.isArray(filterItem.value) ||
          filterItem.value.length === 0 ||
          !filterItem.value
        ) {
          return null;
        }

        return ({ value }) => {
          return filterItem.value.includes(value);
        };
      },
      InputComponent: StatusSelector,
    },
  ];
  const theme = useTheme();
  const navigate = useNavigate();

  const [, postAPI] = useAxios(
    {
      method: "POST",
    },
    {
      manual: true,
    }
  );

  const handlePriorityChange = async (bidId: number, newPriority: string) => {
    await postAPI({
      method: "PUT",
      url: `/bid/${bidId}/priority`,
      data: { priority: newPriority }
    });
    refetchBids();
  };

  const handleToggleReserve = async (bidId: number) => {
    await postAPI({
      method: "PUT",
      url: `/bid/${bidId}/toggle-reserve`
    });
    refetchBids();
  };

  return (
    <Table
      initialState={{
        columns: {
          columnVisibilityModel: {
            actions: me?.role !== "AGENCY",
            user: me?.role !== "AGENCY",
          },
        },
        filter: {
          filterModel: {
            items: [
              {
                field: "status",
                operator: "in",
                value: ["AWAITING_ORDER", "ON_ORDER"],
              },
            ],
          },
        },
        sorting: {
          sortModel: [
            { field: 'priority', sort: 'desc' }
          ],
        },
      }}
      processRowUpdate={async (newRow) => {
        await handlePriorityChange(newRow.id, newRow.priority);
        return newRow;
      }}
      getRowClassName={(params) => {
        const olderThanWeek = moment().diff(params.row.created_at, "days") >= 7;
        const isReserved = params.row.is_reserved;

        return [
          olderThanWeek && params.row.status === "ACCEPTED" ? `row-color-danger` : "",
          isReserved ? `row-color-reserved` : ""
        ].join(" ");
      }}
      columns={[
        {
          field: "is_reserved",
          headerName: "",
          width: 50,
          renderCell: ({ row }) => {
            if (row.is_reserved) {
              return (
                <Tooltip title="Oferta Reservada">
                  <LockIcon color="action" />
                </Tooltip>
              );
            }
            return null;
          },
        },
        { field: "id", headerName: "ID", minWidth: 60 },
        {
          field: "created_at",
          headerName: "Data",
          flex: 1,
          maxWidth: 130,
          valueFormatter: ({ value }) => {
            return moment(value).format("DD/MM/YYYY HH:mm:ss");
          },
        },
        {
          field: "user",
          headerName: "Fornecedor",
          flex: 1,
          maxWidth: 120,
          renderCell: ({ row }) => {
            return (
              <div
                style={{
                  display: "flex",
                }}
              >
                {row.inviter_id && (
                  <Fragment>
                    <Tooltip title={`Indicado por ${row.inviter_name}`}>
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                    &nbsp;&nbsp;&nbsp;
                  </Fragment>
                )}
                <Link href={`/admin/usuarios/${row.user_id}`}>{row.user}</Link>
              </div>
            );
          },
        },
        {
          field: "account.holder_name",
          headerName: "Titular",
          flex: 1,
          maxWidth: 120,
          valueGetter: ({ row }) => {
            return row?.account?.holder_name ?? "";
          },
        },
        { field: "company", headerName: "Programa", flex: 1, maxWidth: 130 },
        {
          field: "amount",
          headerName: "Quantidade",
          flex: 1,
          maxWidth: 130,
          renderCell: ({ row, value }) => {
            return (
              <Fragment>
                <Typography variant="body2">
                  {Number(value).toLocaleString("pt-BR")}
                </Typography>
                {!!row.used_miles && (
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    marginLeft="5px"
                    color={theme.palette.error.main}
                  >
                    (-{Number(row.used_miles).toLocaleString("pt-BR")})
                  </Typography>
                )}
              </Fragment>
            );
          },
          valueGetter: ({ row }) => {
            return currency(row.amount).subtract(row.used_miles).value;
          },
        },
        {
          field: "price_per_k",
          headerName: "Valor do Milheiro",
          maxWidth: 130,
          valueFormatter: ({ value }) => {
            return currency(value).format({
              symbol: "R$ ",
              decimal: ",",
              separator: ".",
            });
          },
        },
        {
          field: "pax",
          headerName: "CPF",
          flex: 1,
          maxWidth: 50,
          renderCell: ({ row, value }) => {
            return (
              <Fragment>
                <Typography variant="body2">{value}</Typography>
                {!!row.used_pax && !!row.pax && (
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    marginLeft="5px"
                    color={theme.palette.error.main}
                  >
                    (-{row.used_pax})
                  </Typography>
                )}
              </Fragment>
            );
          },
          valueGetter: ({ value, row }) => {
            return value ? currency(value).subtract(row.used_pax).value : "∞";
          },
        },
        {
          field: "average_price",
          headerName: "Média por CPF",
          type: "number",
          flex: 1,
          maxWidth: 100,
          valueFormatter(params) {
            return params.value === Infinity
              ? "∞"
              : formatNumberCompact(params.value);
          },
          valueGetter: ({ row }) => {
            const availableMiles = currency(row.amount).subtract(
              row.used_miles
            ).value;
            const availablePax = row.pax - row.used_pax;

            return !row.pax
              ? Infinity
              : currency(availableMiles).divide(availablePax).value;
          },
        },
        {
          field: "accepts_custom_payment",
          headerName: "Aceita Pag. à Prazo",
          type: "boolean",
          flex: 1,
          maxWidth: 140,
        },
        {
          field: "priority",
          headerName: "Prioridade",
          flex: 1,
          maxWidth: 140,
          sortComparator: (v1, v2) => {
            const order1 = PRIORITY_OPTIONS.find(opt => opt.value === v1)?.order || 0;
            const order2 = PRIORITY_OPTIONS.find(opt => opt.value === v2)?.order || 0;
            return order1 - order2;
          },
          renderCell: ({ row }) => {
            return (
              <Input
                variant="standard"
                select
                value={row.priority}
                options={PRIORITY_OPTIONS}
                onChange={async (e) => {
                  await handlePriorityChange(row.id, e.target.value);
                }}
                disabled={me?.role !== 'ADMIN'}
                sx={{
                  '& .MuiInput-underline:before': {
                    borderBottom: 'none',
                  },
                  '& .MuiInput-underline:disabled': {
                    borderBottom: 'none',
                  },
                  '& .MuiInput-underline:after': {
                    borderBottom: 'none',
                  },
                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                    borderBottom: 'none',
                  },
                }}
              />
            );
          },
          filterOperators: prioritySelectorOperator,
        },
        {
          field: "status",
          headerName: "Status",
          flex: 1,
          maxWidth: 140,
          valueFormatter: ({ value }) => {
            return getBidStatusLabel(value);
          },
          renderCell: ({ formattedValue, row }) => {
            return (
              <Chip
                color={getBidStatusColor(row.status)}
                label={formattedValue}
              />
            );
          },
          filterOperators: statusSelectorOperator,
        },
        {
          field: "actions",
          headerName: "",
          hideable: false,
          width: 200,
          align: "right",
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: ({ row }) => {
            const messageText = encodeURI(
              `Olá, ${row.user}! Tudo bem? Me chamo ${me?.name} e estou entrando em contato para falar da sua oferta número ${row.id} na ${APP_NAME}.`
            );

            let menuOptions = [
              {
                label: "Ver Detalhes",
                onClick: async () => {
                  navigate(`/admin/ofertas/${row.id}`);
                },
              },
            ];

            if (me?.role === "ADMIN") {
              menuOptions.push({
                label: row.is_reserved ? "Liberar Oferta" : "Reservar Oferta",
                onClick: async () => {
                  await handleToggleReserve(row.id);
                },
              });
            }

            if (row.status !== "ON_ORDER" && !row.is_reserved) {
              menuOptions.push({
                label: "Aceitar",
                onClick: async () => {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: "purchase",
                      value: currency(
                        currency(row.amount).divide(1000).value
                      ).multiply(row.price_per_k).value,
                      transaction_id: row.id,
                      currency: "BRL",
                      items: [
                        {
                          item_id: row.id,
                          item_name: row.company,
                          price: row.price_per_k,
                          quantity: currency(row.amount).divide(1000).value,
                        },
                      ],
                    },
                  });

                  await postAPI({ url: `/bid/${row.id}/start-order` });
                  refetchBids();
                },
              });
            }

            if (
              row.status === "PENDING" ||
              row.status === "ACCEPTED" ||
              row.status === "AWAITING_ORDER" ||
              row.status === "ON_ORDER"
            ) {
              menuOptions.push({
                label: "Recusar",
                onClick: async () => {
                  setDenyBidId(row.id);
                  denyModalVisibilityControls.setTrue();
                },
              });
            }

            if (row.status === "ACCEPTED" || row.status === "DENIED") {
              menuOptions.push({
                label: "Excluir",
                onClick: async () => {
                  setDeleteBidId(row.id);
                  deleteModalVisibilityControls.setTrue();
                },
              });
            }

            if (row.status === "AWAITING_ORDER" || row.status === "ON_ORDER") {
              menuOptions.push({
                label: "Ver Conta",
                onClick: async () => {
                  fetchBid({
                    url: `/bid/${row.id}`,
                  });

                  accountModalVisibilityControls.setTrue();
                },
              });

              if (!row.is_reserved) {
                menuOptions.push({
                  label: "Emitir",
                  onClick: async () => {
                    navigate(`/admin/ofertas/${row.id}/emissao`);
                  },
                });
              }
            }

            if (row.status === "ON_ORDER") {
              menuOptions.push({
                label: "Finalizar",
                onClick: async () => {
                  await postAPI({ url: `/bid/${row.id}/finish` });
                  refetchBids();
                },
              });
            }

            if (me?.role === "SELLER") {
              menuOptions = menuOptions.filter((item) => {
                return item.label === "Emitir" || item.label === "Ver Conta";
              });
            }

            return (
              <ButtonGroup>
                <Button
                  variant="outlined"
                  onClick={() =>
                    window.open(
                      `https://api.whatsapp.com/send?phone=${row.whatsapp}&text=${messageText}`,
                      "_blank"
                    )
                  }
                >
                  <WhatsAppIcon />
                </Button>
                <Button
                  variant="outlined"
                  onClick={() =>
                    window.open(
                      `https://t.me/${row.telegram.replace(
                        "@",
                        ""
                      )}?text=${messageText}`,
                      "_blank"
                    )
                  }
                  disabled={!row.telegram}
                >
                  <TelegramIcon />
                </Button>
                {["ADMIN", "SELLER"].includes(me?.role as string) && (
                  <Menu options={menuOptions}>
                    <MoreHorizIcon />
                  </Menu>
                )}
              </ButtonGroup>
            );
          },
        },
      ]}
      rows={
        bids?.data?.map((item: any) => ({
          ...item,
          id: item.id,
          user: me?.role === "AGENCY" ? "" : item.user.name,
          user_id: me?.role === "AGENCY" ? "" : item.user.id,
          inviter_id: me?.role === "AGENCY" ? "" : item.user.inviter?.id,
          inviter_name: me?.role === "AGENCY" ? "" : item.user.inviter?.name,
          created_at: item.created_at,
          company: COMPANY_OPTIONS.find((cia) => cia.value === item.company)
            ?.label,
          amount: item.amount,
          used_miles: item?.orders
            ?.filter((item: { confirmation: string }) =>
              ["PENDING", "CONFIRMED"].includes(item.confirmation)
            )
            ?.reduce(
              (prev: number, curr: { miles: number }) =>
                currency(prev).add(curr.miles).value,
              0
            ),
          used_pax: item?.orders
            ?.filter((item: { confirmation: string }) =>
              ["PENDING", "CONFIRMED"].includes(item.confirmation)
            )
            ?.reduce(
              (prev: number, curr: { pax: number }) =>
                currency(prev).add(curr.pax).value,
              0
            ),
          price_per_k: item.price_per_k,
          pax: item.pax ? Number(item.pax) : null,
          status: item.status,
          priority: item.priority,
          whatsapp: me?.role === "AGENCY" ? "" : item.user.whatsapp,
          telegram: me?.role === "AGENCY" ? "" : item.user.telegram,
          address_city: me?.role === "AGENCY" ? "" : item.user.address_city,
          email: me?.role === "AGENCY" ? "" : item.user.email,
          address_state: me?.role === "AGENCY" ? "" : item.user.address_state,
          is_reserved: item.is_reserved,
        })) || []
      }
    />
  );
};
